import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "@shared/client/components/toast/show-toast";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faCheck, faLayerGroup, faMagnifyingGlass, faPlus, } from "@fortawesome/pro-solid-svg-icons";
import { collectionStore } from "@/data/collection-store";
import { WipBorder } from "@/components/wip-border/wip-border";
import { track } from "@/lib/analytics";
import "./add-to-collection-popover.scss";
const MAX_COLLECTION_TITLE_LENGTH = 200;
const MAX_SEARCH_QUERY_LENGTH = 200;
function displayTitleForCollectionId(items, collectionId) {
    const row = items.find((i) => i.collection.id === collectionId);
    const t = row?.collection.title?.trim();
    return t !== undefined && t.length > 0 ? t : "Untitled";
}
export const AddToCollectionPopover = memo(({ blockIds, onClose, onAdded, }) => {
    const ref = useRef(null);
    const { items: eligibleCollections, isLoading } = collectionStore.useListAllWritable();
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [isCreatePending, setIsCreatePending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const trimmedQuery = searchQuery.trim();
    const visibleCollections = useMemo(() => {
        if (trimmedQuery.length === 0)
            return eligibleCollections;
        const needle = trimmedQuery.toLowerCase();
        return eligibleCollections.filter(({ collection, groupTitle }) => {
            const title = (collection.title ?? "Untitled").toLowerCase();
            if (title.includes(needle))
                return true;
            // Also match against the parent Group's title so a user can find
            // "the design collection in My Workspace" by typing either name.
            if (groupTitle !== undefined && groupTitle.toLowerCase().includes(needle))
                return true;
            return false;
        });
    }, [eligibleCollections, trimmedQuery]);
    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);
    const toggle = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    const handleCreateSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = newTitle.trim();
        if (trimmed.length === 0)
            return;
        setIsCreatePending(true);
        try {
            const id = await collectionStore.create({ title: trimmed });
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.add(id);
                return next;
            });
            setNewTitle("");
            setIsCreating(false);
        }
        catch {
            showToast.error("Failed to create collection");
        }
        finally {
            setIsCreatePending(false);
        }
    }, [newTitle]);
    const handleAdd = useCallback(async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0)
            return;
        if (blockIds.length === 0)
            return;
        setIsSubmitting(true);
        track({
            name: "block.added_to_collection",
            props: { collection_count: ids.length, block_count: blockIds.length },
        });
        const results = await Promise.allSettled(ids.map((collectionId) => collectionStore.copyBlocks(collectionId, blockIds)));
        const failures = results.filter((r) => r.status === "rejected").length;
        const successes = ids.length - failures;
        const blockCount = blockIds.length;
        const blockPhrase = blockCount === 1 ? "block" : `${blockCount} blocks`;
        const soleTargetId = ids.length === 1 ? ids[0] : undefined;
        if (failures === 0) {
            showToast.success(successes === 1 && soleTargetId !== undefined
                ? `Added ${blockPhrase} to "${displayTitleForCollectionId(eligibleCollections, soleTargetId)}"`
                : `Added ${blockPhrase} to ${successes} collections`);
        }
        else if (successes === 0) {
            showToast.error("Failed to add to collections");
        }
        else {
            showToast.warning(`Added ${blockPhrase} to ${successes} of ${ids.length} collections`);
        }
        setIsSubmitting(false);
        if (failures < ids.length) {
            onAdded?.();
        }
        onClose();
    }, [blockIds, eligibleCollections, selectedIds, onClose, onAdded]);
    const selectedCount = selectedIds.size;
    const addLabel = selectedCount <= 1 ? "Add" : `Add to ${selectedCount}`;
    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === "Escape" && searchQuery.length > 0) {
            e.preventDefault();
            // Block the popover's document-level Escape listener (lines below)
            // so a first Escape only clears the filter; a second Escape closes.
            e.nativeEvent.stopImmediatePropagation();
            setSearchQuery("");
        }
    }, [searchQuery]);
    return (_jsxs("div", { className: "add-to-collection-popover", ref: ref, role: "dialog", "aria-label": "Add to collections", children: [_jsx(WipBorder, {}), _jsx("div", { className: "add-to-collection-popover__header", children: "Add to collections" }), eligibleCollections.length > 0 ? (_jsxs("div", { className: "add-to-collection-popover__search", children: [_jsx(FaIcon, { icon: faMagnifyingGlass, className: "add-to-collection-popover__search-icon" }), _jsx("input", { type: "text", className: "add-to-collection-popover__search-input", placeholder: "Filter collections\u2026", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onKeyDown: handleSearchKeyDown, maxLength: MAX_SEARCH_QUERY_LENGTH, "aria-label": "Filter collections", autoFocus: true })] })) : null, _jsx("div", { className: "add-to-collection-popover__list", children: isLoading && eligibleCollections.length === 0 ? (_jsx("div", { className: "add-to-collection-popover__empty", children: "Loading\u2026" })) : eligibleCollections.length === 0 ? (_jsx("div", { className: "add-to-collection-popover__empty", children: "No collections yet" })) : visibleCollections.length === 0 ? (_jsxs("div", { className: "add-to-collection-popover__empty", children: ["No collections match \u201C", trimmedQuery, "\u201D"] })) : (visibleCollections.map(({ collection, groupTitle }) => {
                    const checked = selectedIds.has(collection.id);
                    const title = collection.title || "Untitled";
                    const isGroupRooted = groupTitle !== undefined;
                    // Group-rooted Collections attribute to their Group; standalone
                    // Collections that have been shared show the legacy "shared"
                    // pill; private (Primary-rooted) Collections get no badge.
                    const isStandaloneShared = isGroupRooted === false && collection.type === "shared";
                    return (_jsxs("button", { type: "button", role: "checkbox", "aria-checked": checked, className: [
                            "add-to-collection-popover__item",
                            checked && "add-to-collection-popover__item--checked",
                        ]
                            .filter(Boolean)
                            .join(" "), onClick: () => toggle(collection.id), children: [_jsx("span", { className: "add-to-collection-popover__checkbox", "aria-hidden": true, children: checked ? _jsx(FaIcon, { icon: faCheck }) : null }), _jsx(FaIcon, { icon: faLayerGroup, className: "add-to-collection-popover__item-icon" }), _jsx("span", { className: "add-to-collection-popover__item-label", children: title }), isGroupRooted ? (_jsx("span", { className: "add-to-collection-popover__item-group", title: groupTitle, children: groupTitle })) : isStandaloneShared ? (_jsx("span", { className: "add-to-collection-popover__item-badge", children: "shared" })) : null] }, collection.id));
                })) }), isCreating ? (_jsxs("form", { className: "add-to-collection-popover__create", onSubmit: handleCreateSubmit, children: [_jsx("input", { type: "text", className: "add-to-collection-popover__create-input", placeholder: "New collection name\u2026", value: newTitle, onChange: (e) => setNewTitle(e.target.value), maxLength: MAX_COLLECTION_TITLE_LENGTH, autoFocus: true, disabled: isCreatePending }), _jsx("button", { type: "button", className: "add-to-collection-popover__create-cancel", onClick: () => {
                            setIsCreating(false);
                            setNewTitle("");
                        }, disabled: isCreatePending, children: "Cancel" }), _jsx("button", { type: "submit", className: "add-to-collection-popover__create-submit", disabled: newTitle.trim().length === 0 || isCreatePending, children: isCreatePending ? "…" : "Create" })] })) : (_jsxs("button", { type: "button", className: "add-to-collection-popover__new", onClick: () => setIsCreating(true), children: [_jsx(FaIcon, { icon: faPlus, className: "add-to-collection-popover__new-icon" }), _jsx("span", { children: "New collection" })] })), _jsxs("div", { className: "add-to-collection-popover__footer", children: [_jsx("button", { type: "button", className: "add-to-collection-popover__cancel", onClick: onClose, disabled: isSubmitting, children: "Cancel" }), _jsx("button", { type: "button", className: "add-to-collection-popover__submit", onClick: handleAdd, disabled: selectedCount === 0 || isSubmitting, children: isSubmitting ? "Adding…" : addLabel })] })] }));
});
